import { useSelector } from 'react-redux';

import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import {
    BASE_RESIDUAL_DOLLAR_TESTID,
    BASE_RESIDUAL_GROUP_TESTID,
    BASE_RESIDUAL_GROUPID,
    BASE_RESIDUAL_ID,
    BASE_RESIDUAL_LABEL,
    BASE_RESIDUAL_PERCENT_TESTID,
    BASE_RESIDUAL_VALUE_TESTID,
    RESIDUAL_LABEL
} from '../../../constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { kebabCase } from '@makemydeal/dr-common-utils';
import { useFeatureToggle } from '../../../utils/useFeatureToggle';
import { useHeldSelector } from '../../../hooks/useHeldSelector';
import type { ResidualFieldInputProps } from './useResidualField';

export const useBaseResidualInputProps = (): ResidualFieldInputProps => {
    const enabled = useFeatureToggle('enableAppliedLenderInfo');
    const isLeaseResidualTreatmentEnabled = useFeatureToggle('enableLeaseResidualTreatment_F286227');

    const isUserProgramQuotes = useSelector(offerReduxSelectors.getIsUserProgramQuotes);
    const isUnifiedResidualFieldsEnabled = useSelector(featureToggleSelectors.isUnifiedResidualFieldsEnabled);

    const showDisabled = isLeaseResidualTreatmentEnabled ? false : (enabled && !isUserProgramQuotes) || !enabled;

    const { baseResidualType, programResidualPercent, programResidual } = useSelector(
        offerSelectors.getResidualInfoOverrideWithFallback
    );

    const programResidualValue = useHeldSelector(offerReduxSelectors.getProgramResidual);
    const residualPercent = useHeldSelector(offerReduxSelectors.getProgramResidualPercent);

    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isPaymentGridOpen = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, LEASE));
    const isReadOnlyForOpenGrid = isExplicitLenderP1Enabled && isPaymentGridOpen;
    const activeColumnIndex = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex);
    const activeColumnType = useSelector((state: StateTree) =>
        activeColumnIndex != null ? offerReduxSelectors.getPaymentGridResiduals(state)[activeColumnIndex]?.type : undefined
    );
    const activeColumnDollar = useSelector((state: StateTree) =>
        activeColumnIndex != null
            ? offerReduxSelectors.getPaymentGridResiduals(state, { [activeColumnIndex]: RESIDUAL_DOLLAR })[activeColumnIndex]?.val
            : undefined
    );
    const activeColumnPercent = useSelector((state: StateTree) =>
        activeColumnIndex != null
            ? offerReduxSelectors.getPaymentGridResiduals(state, { [activeColumnIndex]: RESIDUAL_PERCENT })[activeColumnIndex]?.val
            : undefined
    );

    const dollar = programResidualValue || programResidual;
    const percent = residualPercent || programResidualPercent;

    return {
        showDisabled,
        residualType: isReadOnlyForOpenGrid && activeColumnType != null ? activeColumnType : baseResidualType,
        residualAmount: isReadOnlyForOpenGrid && activeColumnDollar != null ? activeColumnDollar : dollar,
        residualPercent: isReadOnlyForOpenGrid && activeColumnPercent != null ? activeColumnPercent : percent,
        updateResidualOverride: offerReduxActionCreators.updateBaseResidualOverride,
        id: BASE_RESIDUAL_ID,
        label: isUnifiedResidualFieldsEnabled ? RESIDUAL_LABEL : BASE_RESIDUAL_LABEL,
        name: kebabCase(BASE_RESIDUAL_LABEL),
        groupId: BASE_RESIDUAL_GROUPID,
        valueTestId: BASE_RESIDUAL_VALUE_TESTID,
        groupTestId: BASE_RESIDUAL_GROUP_TESTID,
        dollarTestId: BASE_RESIDUAL_DOLLAR_TESTID,
        percentTestId: BASE_RESIDUAL_PERCENT_TESTID,
        dollarOverrideAction: offerActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
        percentOverrideAction: offerActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE
    };
};
